/**
 * AdminStickyListLayout
 *
 * Shared sticky filter band + measured table-header offset for admin list pages
 * that scroll the viewport (Audit Events, Operations, Email stats, Customers,
 * Flower / Mailed-card orders, Support tickets).
 *
 * Business rules (must stay in sync across consumers):
 * - Sticky band sits under {@link APP_HEADER_HEIGHT} with an opaque background
 *   so table rows never paint through the filters.
 * - Sticky band stacks above sticky table headers (`z-index`) so column headers
 *   and row cells cannot appear on top of / through the filter chrome.
 * - Table `stickyHeaderOffset` = shell header height + measured band height so
 *   column headers stick directly under the filters (never over them).
 * - Tables spread {@link getAdminTableProps} rather than wiring `style` /
 *   `styles` by hand — under `table-layout: fixed` a cell without containment
 *   paints across the next column instead of widening its own.
 * - Wide tables may widen the page (see {@link getAdminResizableTableStyle}).
 *   The filter band uses sticky `left: 0` + viewport-capped width so horizontal
 *   page scroll moves the table while title/filters stay pinned on screen.
 *   {@link AdminAppShell} keeps the shell header clipped so the avatar does not
 *   ride off-screen.
 * - Do **not** wrap the sticky table in `Paper` / `Card` / `Table.ScrollContainer`
 *   / an `overflow-x: auto` ancestor — non-visible overflow creates a scrollport
 *   so Mantine `stickyHeaderOffset` (viewport-relative) lands mid-table
 *   ("floating" thead with body rows above it).
 */

import {
  type CSSProperties,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Box, Stack, rem, type MantineSpacing } from '@mantine/core';
import { APP_HEADER_HEIGHT } from '../../config/layout';

/** Sticky filter band stacks above sticky thead / body cells. */
const ADMIN_STICKY_FILTER_BAND_Z_INDEX = 12;

/** Sticky table headers sit under the filter band but above scrolling rows. */
const ADMIN_STICKY_TABLE_HEADER_Z_INDEX = 11;

/**
 * Opaque thead cell background so striped rows do not show through sticky headers.
 */
export const ADMIN_STICKY_TABLE_HEADER_TH_STYLE: CSSProperties = {
  backgroundColor: 'var(--mantine-color-gray-1)',
  zIndex: ADMIN_STICKY_TABLE_HEADER_Z_INDEX,
};

/**
 * Mantine Table `styles` for sticky thead that paints correctly with
 * `border-collapse: separate` (see csswg-drafts #3136).
 *
 * `td`/`th` use `overflow: hidden` so long cell text cannot paint into the next
 * column under `table-layout: fixed` (Email stats Owner/To overlap, etc.).
 * Pair long values with Mantine `truncate` / `lineClamp` for an ellipsis.
 */
export function getAdminStickyTableStyles() {
  return {
    table: {
      borderCollapse: 'separate' as const,
      borderSpacing: 0,
    },
    th: {
      ...ADMIN_STICKY_TABLE_HEADER_TH_STYLE,
      overflow: 'hidden',
    },
    td: {
      overflow: 'hidden',
      verticalAlign: 'middle' as const,
    },
  };
}

export interface AdminStickyListLayoutRenderProps {
  /**
   * Pixels from the viewport top where table column headers should stick
   * (`APP_HEADER_HEIGHT` + measured sticky band height).
   */
  stickyHeaderOffsetPx: number;
}

export interface AdminStickyListLayoutProps {
  /** Title, filters, and toolbars — sticks under the shell header and is measured. */
  stickyBand: ReactNode;

  /** Initial band height estimate before ResizeObserver fires. */
  initialBandHeightPx?: number;

  /** Gap between the sticky band and the list body (Mantine Stack). */
  gap?: MantineSpacing;

  /** Receives the live sticky header offset for the table below the band. */
  children: (props: AdminStickyListLayoutRenderProps) => ReactNode;
}

/**
 * Renders a measured sticky filter band and supplies `stickyHeaderOffsetPx`
 * to the list/table body below it.
 */
export function AdminStickyListLayout({
  stickyBand,
  initialBandHeightPx = 280,
  gap = 'md',
  children,
}: AdminStickyListLayoutProps) {
  const stickyBandRef = useRef<HTMLDivElement>(null);
  const [filterBandHeightPx, setFilterBandHeightPx] = useState(initialBandHeightPx);

  /**
   * ResizeObserver keeps thead `top` aligned when filters wrap or the window
   * size changes (viewport scroll + APP_HEADER_HEIGHT).
   */
  useEffect(() => {
    const el = stickyBandRef.current;
    if (!el || typeof ResizeObserver === 'undefined') {
      return;
    }

    const apply = () => {
      const h = Math.round(el.getBoundingClientRect().height);
      if (h > 0) {
        setFilterBandHeightPx(h);
      }
    };

    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);

    return () => ro.disconnect();
  }, []);

  const stickyHeaderOffsetPx = APP_HEADER_HEIGHT + filterBandHeightPx;

  return (
    <Stack gap={gap} style={{ minWidth: 0, width: '100%', maxWidth: '100%' }}>
      <Box
        ref={stickyBandRef}
        data-admin-sticky-filter-band=""
        style={{
          position: 'sticky',
          top: rem(APP_HEADER_HEIGHT),
          // Pin filters when a wide table scrolls the page horizontally.
          left: 0,
          zIndex: ADMIN_STICKY_FILTER_BAND_Z_INDEX,
          // Opaque band so scrolling table cells cannot show through filters.
          backgroundColor: 'var(--mantine-color-gray-1)',
          paddingBottom: 'var(--mantine-spacing-xs)',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
          isolation: 'isolate',
          // Cap to the viewport so a minWidth table does not stretch the band
          // wider than the screen (sticky `left` then keeps that screen-sized
          // chrome pinned while the table scrolls underneath/alongside).
          width: '100%',
          maxWidth: '100vw',
          boxSizing: 'border-box',
        }}
      >
        {stickyBand}
      </Box>
      {children({ stickyHeaderOffsetPx })}
    </Stack>
  );
}
